import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Save, KeyRound, Copy, Check, MessageCircle, AlertTriangle, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { passwordError } from "@/lib/password";
import { ShopProfileFields, profileFromMeta, profilePayload } from "@/components/admin/ShopProfileFields";
import { UnlockLoginButton } from "@/components/admin/ShopAdminActions";
import { ShopModulesEditor } from "@/components/admin/ShopModulesEditor";
import { ShopPlanEditor } from "@/components/admin/ShopPlanEditor";

function slugify(s) {
  return (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function waUrl(phone, text) {
  const digits = (phone || "").replace(/\D/g, "");
  const num = digits.length === 0 ? "" : digits.length <= 11 ? `55${digits}` : digits;
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
}

export function EditShopDialog({ shop, meta, open, onOpenChange, onSaved, onMeta }) {
  const [profile, setProfile] = useState(() => profileFromMeta(meta));
  const [form, setForm] = useState(() => ({
    name: shop?.name || "",
    slug: shop?.slug || "",
    phone: shop?.phone || "",
    is_active: shop?.is_active ?? true,
    owner_name: shop?.owner_name || "",
    owner_email: shop?.owner_email || "",
    owner_phone: shop?.owner_phone || "",
  }));
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [newPwConfirm, setNewPwConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [planMods, setPlanMods] = useState(null);
  const onPlanApplied = (d) => {
    setPlanMods(d.plan?.modules || null);
    setProfile((p) => ({ ...p, plan: d.plan?.name || "", amount: d.amount ?? p.amount }));
    const wa = d.addons.find((a) => a.slug === "whatsapp_auto");
    onMeta?.(shop.id, { ...(meta || {}), modules: d.modules, ...(wa ? { wa_auto_allowed: wa.active } : {}) });
  };

  useEffect(() => {
    if (!open) return;
    setForm({
      name: shop?.name || "",
      slug: shop?.slug || "",
      phone: shop?.phone || "",
      is_active: shop?.is_active ?? true,
      owner_name: shop?.owner_name || "",
      owner_email: shop?.owner_email || "",
      owner_phone: shop?.owner_phone || "",
    });
    setSlugTouched(false);
    setProfile(profileFromMeta(meta));
    setNewPw(""); setNewPwConfirm(""); setResetResult(null); setCopied(false);
  }, [open, shop]); // eslint-disable-line react-hooks/exhaustive-deps

  const originalSlug = shop?.slug || "";
  const slugChanged = form.slug && form.slug !== originalSlug;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        phone: form.phone.trim(),
        is_active: form.is_active,
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim(),
        owner_phone: form.owner_phone.trim(),
      };
      const { data } = await api.patch(`/shops/${shop.id}`, payload);
      const { data: m } = await api.put(`/admin/shops/${shop.id}/profile`, profilePayload(profile));
      onMeta?.(shop.id, m);
      toast.success("Oficina atualizada");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (passwordError(newPw)) { toast.error(passwordError(newPw)); return; }
    if (newPw !== newPwConfirm) { toast.error("A confirmação não bate com a nova senha"); return; }
    setResetting(true);
    try {
      const { data } = await api.post(`/shops/${shop.id}/reset-owner-password`, { new_password: newPw });
      setResetResult({
        shop_name: form.name || shop.name,
        owner_email: data?.owner_email || form.owner_email,
        owner_phone: data?.owner_phone || form.owner_phone,
        slug: data?.slug || shop.slug,
        new_password: newPw,
      });
      toast.success("Senha redefinida. E-mail enviado ao dono.");
      setNewPw(""); setNewPwConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setResetting(false); }
  };

  const resetText = resetResult && (() => {
    const base = window.location.origin;
    const url = resetResult.slug ? `${base}/o/${resetResult.slug}` : `${base}/login`;
    return `Nova senha de acesso à oficina "${resetResult.shop_name}" no AutoOficina.\n\nAcesse: ${url}\nLogin: ${resetResult.owner_email}\nSenha: ${resetResult.new_password}\n\nRecomendamos trocar a senha após o próximo acesso.`;
  })();

  const copyAccess = async () => {
    try { await navigator.clipboard.writeText(resetText || ""); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.info("Não foi possível copiar automaticamente"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-shop-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Editar oficina</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Ajuste os dados da oficina e do dono. As alterações entram em vigor na hora.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} data-testid="edit-shop-form" className="space-y-3">
          <fieldset className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <legend className="text-[11px] font-mono-caps text-zinc-500 px-2">Oficina</legend>
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Nome da oficina</Label>
              <Input data-testid="edit-shop-name" required value={form.name}
                     onChange={(e) => setForm((p) => ({ ...p, name: e.target.value, slug: slugTouched ? p.slug : slugify(e.target.value) }))}
                     className="mt-1 bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400 flex items-center gap-1">
                <LinkIcon className="h-3 w-3" /> Endereço do link (slug)
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-xs text-zinc-500 truncate">{window.location.origin}/o/</span>
                <Input data-testid="edit-shop-slug" required value={form.slug}
                       onChange={(e) => { setSlugTouched(true); setForm((p) => ({ ...p, slug: slugify(e.target.value) })); }}
                       className="bg-zinc-900 border-zinc-800 flex-1" placeholder="oficina-do-ze" />
              </div>
              {slugChanged && (
                <p data-testid="slug-change-warning" className="text-[11px] text-amber-300 mt-1">
                  <AlertTriangle className="inline h-3 w-3 mr-1 -mt-0.5" />
                  O link antigo <span className="font-mono whitespace-nowrap">/o/{originalSlug}</span> deixa de funcionar. Reenvie o novo link aos clientes.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Telefone/WhatsApp da oficina</Label>
              <Input data-testid="edit-shop-phone" value={form.phone} inputMode="tel"
                     onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                     className="mt-1 bg-zinc-900 border-zinc-800" placeholder="11999998888" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch data-testid="edit-shop-active" checked={form.is_active} onCheckedChange={(v) => setForm((p) => ({ ...p, is_active: v }))} />
              <span className="text-sm text-zinc-300">{form.is_active ? "Oficina ativa" : "Oficina suspensa"}</span>
            </div>
          </fieldset>

          <fieldset className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <legend className="text-[11px] font-mono-caps text-zinc-500 px-2">Dono</legend>
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Nome do dono</Label>
              <Input data-testid="edit-owner-name" required value={form.owner_name}
                     onChange={(e) => setForm((p) => ({ ...p, owner_name: e.target.value }))}
                     className="mt-1 bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">E-mail de login do dono</Label>
              <Input data-testid="edit-owner-email" type="email" required value={form.owner_email}
                     onChange={(e) => setForm((p) => ({ ...p, owner_email: e.target.value }))}
                     className="mt-1 bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">WhatsApp do dono</Label>
              <Input data-testid="edit-owner-phone" value={form.owner_phone} inputMode="tel"
                     onChange={(e) => setForm((p) => ({ ...p, owner_phone: e.target.value }))}
                     className="mt-1 bg-zinc-900 border-zinc-800" placeholder="11999998888" />
            </div>
          </fieldset>

          <ShopProfileFields form={profile} setForm={setProfile} addons={meta?.addons || []} />

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" disabled={saving} onClick={() => onOpenChange(false)}
                    className="text-zinc-300 hover:bg-zinc-900">Cancelar</Button>
            <Button data-testid="edit-shop-save" type="submit" disabled={saving}
                    className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Salvar</>)}
            </Button>
          </div>
        </form>

        <div className="rounded-xl border border-amber-brand/40 bg-amber-brand/5 p-4 mt-2 space-y-3">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-amber-brand" />
            <span className="text-sm font-bold text-amber-200">Trocar senha do dono</span>
            {meta?.login_locked && <span data-testid="edit-login-locked" className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border bg-red-950/40 text-red-300 border-red-900">Login bloqueado</span>}
          </div>
          <UnlockLoginButton shop={shop} meta={meta} onMeta={onMeta} testid="edit-unlock-login" />
          <p className="text-[11px] text-zinc-500">Trocar a senha aqui também desbloqueia o login na hora.</p>
          {resetResult ? (
            <div data-testid="edit-reset-result" className="space-y-2">
              <div className="text-xs text-amber-200/90">E-mail de aviso enviado para <span className="font-mono">{resetResult.owner_email}</span>.</div>
              <code data-testid="edit-reset-password-value" className="block bg-zinc-950 border border-amber-brand/60 text-amber-brand rounded px-3 py-2 font-mono normal-case font-bold tracking-wider break-all">
                {resetResult.new_password}
              </code>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="edit-reset-copy" type="button" variant="outline" onClick={copyAccess}
                        className="h-10 bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800 font-bold uppercase text-xs">
                  {copied ? <><Check className="h-4 w-4 mr-2 text-green-brand" /> Copiado</> : <><Copy className="h-4 w-4 mr-2" /> Copiar dados</>}
                </Button>
                <a data-testid="edit-reset-whatsapp" href={waUrl(resetResult.owner_phone, resetText || "")}
                   target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center justify-center h-10 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase">
                  <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={submitReset} data-testid="edit-reset-form" className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input data-testid="edit-reset-password" type="text" minLength={8} required value={newPw}
                       onChange={(e) => setNewPw(e.target.value)}
                       placeholder="Nova senha (mín. 8, letra e número)"
                       className="bg-zinc-900 border-zinc-800" />
                <Input data-testid="edit-reset-password-confirm" type="text" minLength={8} required value={newPwConfirm}
                       onChange={(e) => setNewPwConfirm(e.target.value)}
                       placeholder="Confirmar"
                       className="bg-zinc-900 border-zinc-800" />
              </div>
              <Button data-testid="edit-reset-submit" type="submit" disabled={resetting}
                      className="w-full h-10 bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase">
                {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redefinir e enviar por e-mail"}
              </Button>
              <p className="text-[11px] text-amber-200/70">O dono recebe os dados por e-mail. Depois você também pode copiar ou enviar pelo WhatsApp.</p>
            </form>
          )}
        </div>
        {open && <ShopPlanEditor shopId={shop?.id} onApplied={onPlanApplied} onLoaded={(d) => setPlanMods(d.plan?.modules || null)} />}
        <ShopModulesEditor shopId={shop?.id} initial={meta?.modules} planModules={planMods} onSaved={(m) => onMeta?.(shop.id, { ...(meta || {}), modules: m })}
          waAllowed={meta?.wa_auto_allowed} onWaSaved={(v) => onMeta?.(shop.id, { ...(meta || {}), wa_auto_allowed: v })} />
      </DialogContent>
    </Dialog>
  );
}
